import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { DrawingManager, GoogleMap, Polygon } from '@react-google-maps/api';
import { useGoogleMaps } from '@/components/GoogleMapsProvider';
import { LatLng } from '@/apis/types';
import { getMapStyles } from '@/common/GoogleMapStyle';
import type { RootState } from '@/redux-toolkit/store';
import './PolygonFenceMap.scss';

export interface PolygonFenceMapProps {
  center: LatLng;
  polygon: LatLng[];
  closed: boolean;
  resetTrigger: number;
  onPolygonChange: (polygon: LatLng[]) => void;
  onClosedChange: (closed: boolean) => void;
}

const mapContainerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
};

const toPath = (path: google.maps.MVCArray<google.maps.LatLng>): LatLng[] =>
  path.getArray().map((latLng) => ({
    lat: latLng.lat(),
    lng: latLng.lng(),
  }));

const getEventPoint = (
  event: google.maps.MapMouseEvent | google.maps.PolyMouseEvent
): LatLng | null =>
  event.latLng
    ? {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      }
    : null;

// Shared click-to-place polygon editor: click the map to add vertices, click
// the first vertex (or double-click) to close the ring, then drag any vertex
// to edit.
const PolygonFenceMap: React.FC<PolygonFenceMapProps> = ({
  center,
  polygon,
  closed,
  resetTrigger,
  onPolygonChange,
  onClosedChange,
}) => {
  const { isLoaded, loadError } = useGoogleMaps();
  const darkModeEnabled = useSelector(
    (state: RootState) => state.appearance.darkModeEnabled
  );
  const mapRef = useRef<google.maps.Map | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const listenersRef = useRef<google.maps.MapsEventListener[]>([]);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const mapOptions = useMemo<google.maps.MapOptions>(() => {
    if (!isLoaded || typeof google === 'undefined') return {};

    return {
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: google.maps.MapTypeControlStyle?.HORIZONTAL_BAR,
        position: google.maps.ControlPosition?.TOP_LEFT,
      },
      zoomControl: false,
      fullscreenControl: true,
      streetViewControl: true,
      clickableIcons: true,
      styles: getMapStyles(darkModeEnabled),
    };
  }, [isLoaded, darkModeEnabled]);
  const drawingManagerOptions =
    useMemo<google.maps.drawing.DrawingManagerOptions>(() => {
      if (!isLoaded || typeof google === 'undefined') return {};
      const polygonDrawingMode =
        google.maps.drawing?.OverlayType?.POLYGON ??
        ('polygon' as google.maps.drawing.OverlayType);

      return {
        drawingControl: true,
        drawingControlOptions: {
          position: google.maps.ControlPosition?.TOP_CENTER,
          drawingModes: [polygonDrawingMode],
        },
        polygonOptions: {
          fillColor: '#2196F3',
          fillOpacity: 0.35,
          strokeColor: '#0958d9',
          strokeOpacity: 0.95,
          strokeWeight: 2,
          clickable: true,
          editable: true,
          draggable: false,
          geodesic: true,
          zIndex: 2,
        },
      };
    }, [isLoaded]);

  useEffect(() => {
    return () => {
      listenersRef.current.forEach((listener) => listener.remove());
      listenersRef.current = [];
    };
  }, []);

  const syncViewport = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    // Some hosts mount this inside a modal that starts closed, so the
    // container can still be zero-sized on first sync — fitBounds/panTo
    // against a collapsed container computes a zoom that never recovers.
    // Skip until the ResizeObserver below reports a real size.
    const div = map.getDiv();
    if (!div || div.clientWidth === 0 || div.clientHeight === 0) return;

    if (polygon.length === 0) {
      map.panTo(center);
      map.setZoom(20);
      return;
    }

    if (polygon.length === 1) {
      map.panTo(polygon[0]);
      map.setZoom(20);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    polygon.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, 48);
  }, [center, polygon]);

  const syncViewportRef = useRef(syncViewport);
  useEffect(() => {
    syncViewportRef.current = syncViewport;
  }, [syncViewport]);

  useEffect(() => {
    if (!isLoaded) return;
    syncViewport();
  }, [isLoaded, syncViewport, resetTrigger]);

  const updateEditablePath = () => {
    if (!polygonRef.current) return;
    onPolygonChange(toPath(polygonRef.current.getPath()));
  };

  const registerPathListeners = (target: google.maps.Polygon) => {
    listenersRef.current.forEach((listener) => listener.remove());
    const path = target.getPath();
    listenersRef.current = [
      path.addListener('set_at', updateEditablePath),
      path.addListener('insert_at', updateEditablePath),
      path.addListener('remove_at', updateEditablePath),
    ];
  };

  const isFirstVertexClick = (point: LatLng) => {
    if (polygon.length < 3) return false;
    const firstPoint = polygon[0];
    const latDiff = Math.abs(firstPoint.lat - point.lat);
    const lngDiff = Math.abs(firstPoint.lng - point.lng);

    return latDiff <= 0.00008 && lngDiff <= 0.00008;
  };

  const handleDrawingClick = (
    event: google.maps.MapMouseEvent | google.maps.PolyMouseEvent
  ) => {
    if (closed) return;
    const nextPoint = getEventPoint(event);
    if (!nextPoint) return;

    if (isFirstVertexClick(nextPoint)) {
      onClosedChange(true);
      return;
    }

    onPolygonChange([...polygon, nextPoint]);
  };

  const handleDrawingManagerPolygonComplete = (
    completedPolygon: google.maps.Polygon
  ) => {
    const completedPath = toPath(completedPolygon.getPath());
    completedPolygon.setMap(null);

    if (completedPath.length > 0) {
      onPolygonChange(completedPath);
      onClosedChange(completedPath.length >= 3);
    }
  };

  if (loadError) {
    return (
      <div className='polygon-map-state'>
        Unable to load map. Please try again.
      </div>
    );
  }

  if (!isLoaded) {
    return <div className='polygon-map-state'>Loading map...</div>;
  }

  return (
    <GoogleMap
      mapContainerStyle={mapContainerStyle}
      center={center}
      zoom={20}
      options={mapOptions}
      onLoad={(map) => {
        mapRef.current = map;

        const div = map.getDiv();
        if (div && typeof ResizeObserver !== 'undefined') {
          const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (
              entry &&
              entry.contentRect.width > 0 &&
              entry.contentRect.height > 0
            ) {
              google.maps.event.trigger(map, 'resize');
              syncViewportRef.current();
            }
          });
          observer.observe(div);
          resizeObserverRef.current = observer;
        }
      }}
      onUnmount={() => {
        resizeObserverRef.current?.disconnect();
        resizeObserverRef.current = null;
        mapRef.current = null;
      }}
      onClick={handleDrawingClick}
      onDblClick={() => {
        if (polygon.length >= 3) {
          onClosedChange(true);
        }
      }}
    >
      {polygon.length > 0 && (
        <Polygon
          key={`${resetTrigger}-${closed ? 'closed' : 'drawing'}`}
          paths={polygon}
          onLoad={(loadedPolygon) => {
            polygonRef.current = loadedPolygon;
            if (closed) {
              registerPathListeners(loadedPolygon);
            }
          }}
          onUnmount={(unmountedPolygon) => {
            if (polygonRef.current === unmountedPolygon) {
              polygonRef.current = null;
            }
            listenersRef.current.forEach((listener) => listener.remove());
            listenersRef.current = [];
          }}
          onClick={handleDrawingClick}
          options={{
            fillColor: '#2196F3',
            fillOpacity: polygon.length >= 3 ? 0.35 : 0.12,
            strokeColor: '#0958d9',
            strokeOpacity: 0.95,
            strokeWeight: 2,
            clickable: true,
            editable: closed,
            draggable: false,
            geodesic: true,
            zIndex: 2,
          }}
        />
      )}
      <DrawingManager
        onPolygonComplete={handleDrawingManagerPolygonComplete}
        options={drawingManagerOptions}
      />
    </GoogleMap>
  );
};

export default PolygonFenceMap;
